// @ts-nocheck
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";
import { generateServiceSchema } from "@/utils/schemaMarkup";
import Layout from "@/components/Layout";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, X, FileSearch, Star } from "lucide-react";
import { motion } from "framer-motion";

const appraisalFormSchema = z.object({
  artworkTitle: z
    .string()
    .min(2, { message: "Artwork title must be at least 2 characters long" })
    .max(100, { message: "Artwork title cannot exceed 100 characters" }),
  artistName: z
    .string()
    .min(2, { message: "Artist name must be at least 2 characters long" })
    .max(100, { message: "Artist name cannot exceed 100 characters" }),
  dimensions: z
    .string()
    .min(3, { message: "Please provide artwork dimensions" })
    .max(50, { message: "Dimensions cannot exceed 50 characters" }),
  medium: z
    .string()
    .min(2, { message: "Please specify the medium" })
    .max(50, { message: "Medium cannot exceed 50 characters" }),
  yearCreated: z
    .string()
    .optional(),
  condition: z
    .string()
    .min(10, { message: "Please provide a detailed condition description (minimum 10 characters)" })
    .max(500, { message: "Condition description cannot exceed 500 characters" }),
  description: z
    .string()
    .min(20, { message: "Description must be at least 20 characters long" })
    .max(1000, { message: "Description cannot exceed 1000 characters" }),
  appraisalPurpose: z
    .string()
    .min(1, { message: "Please select the purpose of appraisal" }),
  estimatedValue: z
    .string()
    .optional(),
  provenance: z
    .string()
    .max(500, { message: "Provenance information cannot exceed 500 characters" })
    .optional(),
});

type AppraisalFormValues = z.infer<typeof appraisalFormSchema>;

const Appraisal = () => {
  // SEO with Service schema for appraisal service
  const serviceSchema = generateServiceSchema({
    name: "Florida Highwaymen Art Appraisal Services",
    description: "Professional Florida Highwaymen artwork appraisal and authentication by certified experts. Accurate valuations for insurance, estate planning, or sale purposes.",
    url: "/appraisal",
    serviceType: "Art Appraisal and Authentication",
    areaServed: "United States"
  });

  useSEO({
    ...SEO_CONFIGS.appraisal,
    additionalSchema: serviceSchema
  });

  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const form = useForm<AppraisalFormValues>({
    resolver: zodResolver(appraisalFormSchema),
    defaultValues: {
      artworkTitle: "",
      artistName: "",
      dimensions: "",
      medium: "",
      yearCreated: "",
      condition: "",
      description: "",
      appraisalPurpose: "",
      estimatedValue: "",
      provenance: "",
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024 // 10MB limit
    );
    
    if (validFiles.length !== files.length) {
      toast({
        title: "Invalid files",
        description: "Only image files under 10MB are allowed",
        variant: "destructive",
      });
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 images
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: AppraisalFormValues) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to submit an appraisal request",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    setSubmitting(true);
    try {
      // Upload images if any
      const imageUrls: string[] = [];
      
      for (const file of selectedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('appraisal-images')
          .upload(fileName, file);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('appraisal-images')
          .getPublicUrl(fileName);
          
        imageUrls.push(publicUrl);
      }

      // Create appraisal request
      const { error } = await supabase.from("appraisal_requests").insert({
        user_id: user.id,
        artwork_title: values.artworkTitle,
        artist_name: values.artistName,
        dimensions: values.dimensions,
        medium: values.medium,
        year_created: values.yearCreated || null,
        condition_description: values.condition,
        description: values.description,
        appraisal_purpose: values.appraisalPurpose,
        estimated_value: values.estimatedValue || null,
        provenance: values.provenance || null,
        image_urls: imageUrls,
        status: 'pending'
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your appraisal request has been submitted. We'll get back to you within 3-5 business days.",
      });
      
      form.reset();
      setSelectedFiles([]);
    } catch (error: any) {
      console.error("Error submitting appraisal request:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit your appraisal request",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect to login page if not logged in
  React.useEffect(() => {
    if (!isLoading && !user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to request an appraisal.",
        variant: "destructive",
      });
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
          <div className="flex justify-center items-center h-64">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center space-y-4"
            >
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
              <p className="text-white/70 font-light">Loading...</p>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
        {/* Background lighting effects */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute top-20 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-20 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: 2,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="container max-w-4xl py-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Card className="relative overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-blue-500/10" />
              
              <CardHeader className="relative bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-blue-600/20 backdrop-blur-xl border-b border-white/10">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-400/30 backdrop-blur-xl">
                    <FileSearch className="h-8 w-8 text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-3xl font-light text-white tracking-wide">
                      Artwork Appraisal Request
                    </CardTitle>
                    <CardDescription className="text-white/80 font-light mt-2 leading-relaxed">
                      Get a professional appraisal for your Florida Highwaymen artwork. Our certified appraisers will provide you with a detailed evaluation within 3-5 business days.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="relative p-8">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-2 gap-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="artworkTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Artwork Title *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter the artwork title" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="artistName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Artist Name *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter the artist's name" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-3 gap-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="dimensions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Dimensions *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., 24 x 30 inches" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="medium"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Medium *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., Oil on board" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="yearCreated"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Year Created</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., 1965" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-2 gap-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="appraisalPurpose"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Purpose of Appraisal *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="bg-white/10 backdrop-blur-xl border border-white/20 text-white focus:border-emerald-400/50 focus:ring-emerald-400/20">
                                  <SelectValue placeholder="Select purpose" className="text-white/50" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-slate-950/95 backdrop-blur-xl border border-white/10">
                                <SelectItem value="insurance" className="text-white hover:bg-white/10">Insurance</SelectItem>
                                <SelectItem value="estate" className="text-white hover:bg-white/10">Estate Planning</SelectItem>
                                <SelectItem value="sale" className="text-white hover:bg-white/10">Sale/Market Value</SelectItem>
                                <SelectItem value="donation" className="text-white hover:bg-white/10">Donation/Tax</SelectItem>
                                <SelectItem value="curiosity" className="text-white hover:bg-white/10">Personal Knowledge</SelectItem>
                                <SelectItem value="legal" className="text-white hover:bg-white/10">Legal Proceedings</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="estimatedValue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Your Estimated Value</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g., $5,000 - $10,000" 
                                {...field} 
                                className="bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20"
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="condition"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Condition Description *</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Describe the current condition of the artwork (any damage, restoration, etc.)"
                                className="min-h-[100px] bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20 resize-none"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Artwork Description *</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Provide a detailed description of the artwork (subject matter, colors, style, etc.)"
                                className="min-h-[120px] bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20 resize-none"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7, duration: 0.6 }}
                    >
                      <FormField
                        control={form.control}
                        name="provenance"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-light">Provenance (Optional)</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="History of ownership, exhibitions, publications, etc."
                                className="min-h-[80px] bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400/50 focus:ring-emerald-400/20 resize-none"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage className="text-red-400" />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div 
                      className="space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8, duration: 0.6 }}
                    >
                      <div>
                        <label className="block text-sm font-light mb-3 text-white">
                          Artwork Images (Up to 5 images, max 10MB each)
                        </label>
                        <div className="border-2 border-dashed border-white/20 rounded-2xl p-8 text-center bg-white/5 backdrop-blur-xl hover:border-emerald-400/50 transition-colors duration-300">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className="cursor-pointer flex flex-col items-center justify-center"
                          >
                            <Upload className="h-12 w-12 text-emerald-400 mb-3" />
                            <span className="text-sm text-white/70 font-light">
                              Click to upload images or drag and drop
                            </span>
                          </label>
                        </div>
                      </div>

                      {selectedFiles.length > 0 && (
                        <motion.div 
                          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5 }}
                        >
                          {selectedFiles.map((file, index) => (
                            <motion.div 
                              key={index} 
                              className="relative group"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1, duration: 0.3 }}
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-24 object-cover rounded-xl border border-white/20"
                              />
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors backdrop-blur-xl opacity-0 group-hover:opacity-100"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9, duration: 0.6 }}
                    >
                      <Button 
                        type="submit" 
                        className="w-full py-4 text-lg bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500/30 backdrop-blur-xl transition-all duration-200 hover:shadow-lg hover:shadow-emerald-500/25"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 
                            Submitting Request...
                          </>
                        ) : (
                          "Submit Appraisal Request"
                        )}
                      </Button>
                    </motion.div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Appraisal; 